import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Bell,
  BarChart3,
  TrendingUp,
  Trophy,
  ClipboardList,
  LogOut,
  ChevronRight,
  User,
  Users,
  Settings,
  Home,
  Shield,
  HelpCircle,
  Grid3X3,
  GraduationCap
} from "lucide-react";
import { logout, isAdvisor, getUserRole } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";

export default function MobileMorePage() {
  const navigate = useNavigate();
  const canManage = isAdvisor();
  const userRole = getUserRole();

  const handleLogout = () => {
    logout();
    navigate("/advisor/login");
  };

  const menuItems = [
    { 
      icon: Bell, 
      label: "Renewals", 
      description: "Policy renewal reminders",
      path: "/advisor/renewals",
      color: "text-[#2E3B57]",
      bgColor: "bg-[#EDF1F8]/30"
    },
    { 
      icon: BarChart3, 
      label: "Analytics", 
      description: "Business insights & reports",
      path: "/advisor/business",
      color: "text-purple-600",
      bgColor: "bg-purple-500/10"
    },
    { 
      icon: GraduationCap, 
      label: "Training Academy", 
      description: "Learn insurance from scratch",
      path: "/advisor/training",
      color: "text-orange-600",
      bgColor: "bg-orange-500/10"
    },
    { 
      icon: ClipboardList, 
      label: "Create Quote", 
      description: "Generate health insurance quotes",
      path: "/advisor/quotes/new",
      color: "text-[#A87608]",
      bgColor: "bg-[#D4A017]/10"
    }
  ];

  // Admin-only items
  const adminItems = [
    { 
      icon: Trophy, 
      label: "Contests", 
      description: "Insurance company contests",
      path: "/advisor/contests",
      color: "text-[#A87608]",
      bgColor: "bg-[#D4A017]/10"
    },
    { 
      icon: TrendingUp, 
      label: "VLI", 
      description: "Variable Life Insurance",
      path: "/advisor/vli",
      color: "text-emerald-600",
      bgColor: "bg-emerald-500/10"
    },
    { 
      icon: Grid3X3, 
      label: "Health Grid", 
      description: "Commission rate grid",
      path: "/advisor/health-grid",
      color: "text-cyan-600",
      bgColor: "bg-cyan-500/10"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-24">
      {/* Mobile Header */}
      <header className="bg-white/80 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-[#1B2A4A]">More</h1>
          <p className="text-[#3A4557] text-sm">
            {canManage ? 'Admin' : 'Employee'} • {userRole === 'advisor' ? 'Full Access' : 'Limited Access'}
          </p>
        </div>
      </header>

      <main className="px-4 py-4 space-y-6">
        {/* User Profile Card */}
        <div className="bg-[#EDF1F8]/50 rounded-2xl p-4 border border-[#C9D4E6]/50">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <User className="h-7 w-7 text-[#0D1A3A]" />
            </div>
            <div className="flex-1">
              <p className="text-[#1B2A4A] font-semibold text-lg">
                {canManage ? 'Prashant K Uunadkat' : 'Operations Assistant'}
              </p>
              <p className="text-[#3A4557] text-sm">
                {canManage ? 'Beshak Verified Advisor' : 'Team Member'}
              </p>
            </div>
            <Shield className="h-6 w-6 text-[#A87608]" />
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-[#3A4557] text-xs font-semibold uppercase tracking-wider mb-3 px-1">
            Quick Actions
          </h2>
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="w-full bg-[#EDF1F8]/30 hover:bg-[#EDF1F8]/50 rounded-xl p-4 flex items-center gap-4 transition-colors border border-[#C9D4E6]/30"
              >
                <div className={`h-12 w-12 rounded-xl ${item.bgColor} flex items-center justify-center`}>
                  <item.icon className={`h-6 w-6 ${item.color}`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-[#1B2A4A] font-medium">{item.label}</p>
                  <p className="text-[#3A4557] text-sm">{item.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-[#3A4557]" />
              </button>
            ))}
          </div>
        </div>

        {/* Admin Only */}
        {canManage && (
          <div>
            <h2 className="text-[#3A4557] text-xs font-semibold uppercase tracking-wider mb-3 px-1">
              Admin Features
            </h2>
            <div className="space-y-2">
              {adminItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="w-full bg-[#EDF1F8]/30 hover:bg-[#EDF1F8]/50 rounded-xl p-4 flex items-center gap-4 transition-colors border border-[#C9D4E6]/30"
                >
                  <div className={`h-12 w-12 rounded-xl ${item.bgColor} flex items-center justify-center`}>
                    <item.icon className={`h-6 w-6 ${item.color}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-[#1B2A4A] font-medium">{item.label}</p>
                    <p className="text-[#3A4557] text-sm">{item.description}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#3A4557]" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Settings & Logout */}
        <div>
          <h2 className="text-[#3A4557] text-xs font-semibold uppercase tracking-wider mb-3 px-1">
            Account
          </h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate("/")}
              className="w-full bg-[#EDF1F8]/30 hover:bg-[#EDF1F8]/50 rounded-xl p-4 flex items-center gap-4 transition-colors border border-[#C9D4E6]/30"
            >
              <div className="h-12 w-12 rounded-xl bg-[#E2E9F4]/50 flex items-center justify-center">
                <Home className="h-6 w-6 text-[#3A4557]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-[#1B2A4A] font-medium">Back to Website</p>
                <p className="text-[#3A4557] text-sm">Visit public website</p>
              </div>
              <ChevronRight className="h-5 w-5 text-[#3A4557]" />
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full bg-red-500/10 hover:bg-red-500/20 rounded-xl p-4 flex items-center gap-4 transition-colors border border-red-500/20"
            >
              <div className="h-12 w-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <LogOut className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-red-600 font-medium">Logout</p>
                <p className="text-red-600/60 text-sm">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      </main>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
